
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useToast } from "@/hooks/use-toast";
import { useFormValidation, ValidationRule } from "@/hooks/useFormValidation";
import EnhancedInput from "@/components/forms/EnhancedInput";

interface NewsletterFormData {
  email: string;
}

const NewsletterSection = () => {
  const { toast } = useToast();
  
  const validationRules: ValidationRule<NewsletterFormData>[] = [
    {
      field: 'email',
      message: 'Please enter a valid email address',
      validate: (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value);
      },
      realTime: true
    }
  ];

  const {
    formData,
    errors,
    isSubmitting,
    updateField,
    validateAll,
    resetForm,
    setSubmitting,
    getFieldProps
  } = useFormValidation<NewsletterFormData>(
    { email: '' },
    validationRules,
    { announceErrors: true }
  );
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateAll()) {
      return;
    }

    setSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Successfully subscribed!",
        description: "Thank you for joining the SOLR constellation.",
        variant: "default",
      });
      
      resetForm();
    } catch (error) {
      toast({
        title: "Subscription failed",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-solr-space-dark/50 to-solr-space-darker opacity-70 z-0"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Join Our <span className="neural-text">Constellation</span>
          </h2>
          <p className="text-solr-starfield-silver mb-8">
            Subscribe to receive exclusive offers, early access to new products, 
            and neural optimization tips from our experts.
          </p>
          
          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1">
                <EnhancedInput
                  type="email"
                  label="Email Address"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="bg-solr-space-dark/50 border-solr-nebula-purple/20 text-solr-starfield-white rounded-full sm:rounded-r-none focus-visible:ring-solr-nebula-purple"
                  showRequired
                  {...getFieldProps('email')}
                />
              </div>
              <Button 
                type="submit" 
                className="btn-primary sm:rounded-l-none h-[44px] disabled:opacity-50" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" variant="dots" />
                ) : (
                  'Subscribe'
                )}
              </Button>
            </div>
            
            <p className="text-xs text-solr-starfield-silver mt-3">
              We respect your privacy and won't share your information. 
              Unsubscribe anytime with one click.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;
